import { Box } from 'grommet';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { route, Router } from 'preact-router';
import { connect, useDispatch } from 'react-redux';
import { Permission } from 'zl-shared';

import AppLoader from '@/components/AppLoader';
import ContactUsModalLoader from '@/components/async/ContactUsModalLoader';
import InstallAppModalLoader from '@/components/async/InstallAppModalLoader';
import AuthGuard from '@/components/Authorize/AuthGuard';
import GuestOnlyGuard from '@/components/Authorize/GuestOnlyGuard';
import HabitatsUpdater from '@/components/HabitatsUpdater';
import ConnectedProductsPrompt from '@/components/products/ProductsPromptModal/redux/connected';
import Redirect from '@/components/Redirect';
import TermsAndConditions from '@/components/TermsAndConditions';
import { PRIVACY_PDF_URL, TERMS_PDF_URL } from '@/components/TermsAndConditions/constants';
import WhatsNew from '@/components/WhatsNew';
import { getRedirectURLLocalStorage, loadPage, logAndGetCampaignData } from '@/helpers';
import { useSubscribeNotificationsHandler } from '@/hooks/notifications';
import { useSelector } from '@/redux/helper';
import { selectAllFreemiumHabitatsMap } from '@/redux/selectors/habitats';
import { setIsRealEmail, setReferralData, setUserData } from '@/redux/slices/user/actions';
// Code-splitting is automated for `routes` directory
import Account from '@/routes/account';
import Album from '@/routes/album';
import CheckoutCancelled from '@/routes/checkout/CheckoutCancelled';
import CheckoutCompleted from '@/routes/checkout/CheckoutCompleted';
import EmailVerify from '@/routes/emailVerify';
import Feed from '@/routes/feed';
import Gift from '@/routes/gift';
import Habitat from '@/routes/habitat';
import Habitats from '@/routes/habitats';
import Help from '@/routes/help';
import Highlights from '@/routes/highlights';
import HomeWithLayout from '@/routes/home';
import Login from '@/routes/login';
import HabitatsView from '@/routes/MapView';
import MyAlbumLayoutDetector from '@/routes/myAlbum/MyAlbumLayoutDetector';
import NotFound from '@/routes/notFound';
import Notifications from '@/routes/notifications';
import PartnerDonation from '@/routes/partnerDonation';
import PasswordReset from '@/routes/passwordReset';
import Redeem from '@/routes/redeem';
import Schedule from '@/routes/schedule';
import ScheduleMobileLayout from '@/routes/schedule/MobileLayout';
import Signup from '@/routes/signup';
import SocialLogin from '@/routes/socialLogin';
import SoftReload from '@/routes/softReload';
import Start from '@/routes/start';
import TalkSchedule from '@/routes/talkSchedule';
import TermsAndPrivacy from '@/routes/TermsAndPrivacy';
import Welcome from '@/routes/welcome';
import { buildURL, patch, post } from '@/shared/fetch';
import { logPageViewGA } from '@/shared/ga';
import { trackMP } from '@/shared/mp';
import { useSlideDownPromptPushCategories, useUpdatePushNotificationData } from '@/shared/pushNotifications/hooks';

import GuestExperienceController from '../GuestExperienceController';
import IframeView from '../IframeView/IframeView';
import { LayoutDetector } from '../LayoutDetector';
import { useDefaultLandingPath } from '../LayoutDetector/hooks';
import ConnectedMembersOnlyWarningModal from '../MembersOnlyWarningModal/redux/connected';
import AdminRouter from './AdminRouter';
import AutoReadNotificationsHandler from './AutoReadNotificationsHandler';

const DefaultLandingRedirect = () => {
  const defaultLandingPath = useDefaultLandingPath();
  return <Redirect to={defaultLandingPath} />;
};

const EducatorRedirect = ({ matches }) => {
  const educatorsAppURL = useSelector((state) => state.remoteConfig.educatorsAppURL);
  useEffect(() => {
    if (educatorsAppURL) {
      const url = new URL(educatorsAppURL);
      url.pathname = `/${matches.educatorPath || ''}`;
      url.search = window.location.search;
      url.hash = window.location.hash;
      loadPage(url.toString());
    }
  }, [matches, educatorsAppURL]);
  return null;
};

const Main = ({ onRouteChange, showContactUs, logged, timezone, showInstallApp }) => {
  const dispatch = useDispatch();
  const [path, setPath] = useState();
  const freemiumHabitatsMap = useSelector(selectAllFreemiumHabitatsMap);

  const isGiftCardUser = useMemo(() => {
    if (typeof document !== 'undefined') {
      const { searchParams } = new URL(window.location);
      return searchParams.get('isGiftCardUser') === 'true';
    }
    return false;
  }, []);

  const defaultLandingPath = useDefaultLandingPath();

  useEffect(() => {
    const updateRefData = async () => {
      const campaignData = await logAndGetCampaignData();
      dispatch(setReferralData(campaignData));
    };
    updateRefData();
  }, [dispatch]);

  useEffect(() => {
    const { timeZone: clientTimezone } = Intl.DateTimeFormat().resolvedOptions();
    if (logged && timezone !== clientTimezone) {
      patch(buildURL('/users/timezone'), { timezone: clientTimezone }).catch((error) =>
        console.error('Failed to update timezone', error),
      );
    }
  }, [logged, timezone]);

  const emailVerificationHandler = useCallback(
    async (token, email, source) => {
      try {
        const { valid } = await post(buildURL('/users/emailVerification'), { token, email, source });
        if (valid) {
          dispatch(setIsRealEmail());
        }
      } catch (err) {
        console.error('email verification error: ', err);
      }
    },
    [dispatch],
  );

  const routerChangeHandler = useCallback(
    (props) => {
      const {
        url,
        current: {
          props: { matches },
        },
      } = props;

      if (url.startsWith('/checkout-completed')) {
        const { passType, price } = matches;
        const redirect = matches['co-redirect'];
        const newPath = `/welcome?plan=${passType}&price=${price}${
          redirect ? `&co-redirect=${encodeURIComponent(redirect)}` : ''
        }`;
        setPath(newPath);
        return;
      }

      if (matches.emailVerificationCode && matches.verificationEmail) {
        emailVerificationHandler(matches.emailVerificationCode, matches.verificationEmail, 'link');
        return;
      }
      setPath(url);
      logPageViewGA(url);
      trackMP('page-view');

      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PageView');
      }

      onRouteChange(props);
    },
    [emailVerificationHandler, onRouteChange],
  );

  let afhParam;
  if (typeof window !== 'undefined' && !window.location.href.includes('socialLogin')) {
    const params = new URLSearchParams(window.location.search);
    const redirectToParam = params.get('redirect');
    afhParam = params.get('utm_afh');
    if (logged && redirectToParam) {
      route(redirectToParam, true);
    }
  }

  useEffect(() => {
    if (logged && afhParam) {
      (async (afhParam) => {
        const newPath = getRedirectURLLocalStorage();

        if (afhParam in freemiumHabitatsMap && freemiumHabitatsMap[afhParam]) {
          route(newPath ?? '/map', true);
          return;
        }
        try {
          const url = buildURL('/users/addFreeHabitat');
          const afhResponse = await post(url, { freeHabitats: [afhParam] });
          if (afhResponse?.user) {
            dispatch(setUserData({ ...afhResponse.user, sessionChecked: true }));
          }
          logPageViewGA('/afh');
          trackMP('afh');
        } catch (err) {
          console.error('Error on afh procedure', err);
          route(newPath ?? '/map', true);
        }
      })(afhParam);
    }
  }, [logged, afhParam, freemiumHabitatsMap, dispatch]);

  useUpdatePushNotificationData();
  useSlideDownPromptPushCategories();

  useSubscribeNotificationsHandler();

  const routes = (
    <Box fill className="calculated-full-height">
      <Router onChange={routerChangeHandler}>
        {/* redirects to /highlights on mobile, /home on desktop */}
        <DefaultLandingRedirect path="/" />
        <DefaultLandingRedirect path="/map" />

        {/* This is an intermediate route to soft reload the page when clicking on a notification */}
        {/* You should use `to` query param to route to the destination */}
        <SoftReload path="/redirect" title="Loading..." />

        {/* Redirect to the educators app (currently only used for checkout process) */}
        <EducatorRedirect path="/educators/:educatorPath*" />

        {/* Render the same component for authenticated and unauthenticated users */}
        <AuthGuard
          path="/home"
          publicPath={false}
          title="Home"
          permission={Permission.Map.View}
          fallback={<HomeWithLayout />}
        >
          <HomeWithLayout />
        </AuthGuard>

        <Redirect path="/prices" to="/home" />

        <SocialLogin path="/socialLogin" />
        <CheckoutCompleted path="/checkout-completed" />
        <CheckoutCancelled path="/checkout-cancelled" />

        <Login path="/login/token/:token" title="Login" />

        <GuestOnlyGuard path="/login" redirectRoute={defaultLandingPath} title="Login">
          <Login />
        </GuestOnlyGuard>

        <GuestOnlyGuard path="/signup" title="Sign Up" redirectRoute={isGiftCardUser ? '/redeem' : defaultLandingPath}>
          <Signup />
        </GuestOnlyGuard>

        <GuestOnlyGuard path="/help" title="Help" fallback={<Help />}>
          <Help />
        </GuestOnlyGuard>

        <PasswordReset path="/passwordReset" title="Reset Password" />

        {/* display 404 instead of Unatuhorized message
            we don't want our viewers to know about this route
            and still redirect unauthenticated users to /login page
            this redirection is there not to confuse our admins */}
        <AuthGuard path="/admin/:*" title="Admin" adminOnly fallback={<NotFound />}>
          <AdminRouter />
        </AuthGuard>

        {/* Render the same component for authenticated and unauthenticated users */}
        <AuthGuard
          path="/highlights"
          publicPath={false}
          title="Highlights"
          permission={Permission.Map.View}
          fallback={<Highlights />}
        >
          <Highlights />
        </AuthGuard>

        <AuthGuard path="/feed" title="Community Feed" permission={Permission.Map.View} redirectTo="/login">
          <Feed />
        </AuthGuard>

        <AuthGuard path="/habitats" title="My Animals" permission={Permission.Map.View} redirectTo="/home">
          <Habitats />
        </AuthGuard>

        <AuthGuard path="/welcome" redirectTo="/home" permission={Permission.Welcome.View}>
          <Welcome />
        </AuthGuard>

        <Habitat path="/h/:zooName/:habitatSlug" skipTitle />

        <AuthGuard path="/notifications" title="Notifications" redirectTo="/home" permission={Permission.Map.View}>
          <Notifications />
        </AuthGuard>
        <AuthGuard
          path="/schedule"
          title="Schedule"
          publicPath="/talk-schedule"
          permission={Permission.Schedule.View}
          redirectTo="/talk-schedule"
        >
          <LayoutDetector desktop={<TalkSchedule />} mobile={<ScheduleMobileLayout />} title="Events" />
        </AuthGuard>
        <GuestOnlyGuard path="/talk-schedule" title="Schedule" redirectRoute="/schedule" overrideHistory>
          <LayoutDetector desktop={<TalkSchedule />} mobile={<ScheduleMobileLayout title="Events" />} />
        </GuestOnlyGuard>
        <GuestOnlyGuard title="My Journal" path="/u/:userName" fallback={<MyAlbumLayoutDetector />}>
          <MyAlbumLayoutDetector />
        </GuestOnlyGuard>
        <AuthGuard path="/redeem" permission={Permission.Redeem.View} redirectTo="/login">
          <Redeem />
        </AuthGuard>
        <AuthGuard path="/emailVerification" permission={Permission.EmailVerifyInput.View} redirectTo="/login">
          <EmailVerify />
        </AuthGuard>
        <Gift path="/gift" />
        <PartnerDonation path="/partnerDonation" title="Partner Donation" />
        <Redirect path="/favorite" to="/habitats" />
        <AuthGuard path="/account/:activeTab" permission={Permission.Profile.Edit} redirectTo="/login">
          <Account />
        </AuthGuard>
        <Redirect path="/plans" to="/start?ppt=premium" />
        <AuthGuard path="/start" permission={Permission.Map.View} redirectTo="/login">
          <Start />
        </AuthGuard>

        {/* deprecated routes, we moved them to webflow */}
        <TermsAndPrivacy path="/terms-and-conditions" title="Terms and Conditions" pdfLink={TERMS_PDF_URL} />
        <TermsAndPrivacy path="/privacy-policy" title="Privacy Policy" pdfLink={PRIVACY_PDF_URL} />

        <Album mediaType="photos" path="/album/photos/:photoId" />
        <Album mediaType="videos" path="/album/videos/:videoId" />

        {/* TODO remove /mapView once its updated on the torontozoo website */}
        {/* Keep for backwards compatibility for partners using this url */}
        <IframeView linkTarget="_blank" path="/mapView" key="habitats" showTrailers>
          <HabitatsView />
        </IframeView>
        <IframeView linkTarget="_blank" path="/embed/map-view" key="embed-habitats" showTrailers>
          <HabitatsView embed />
        </IframeView>
        <IframeView path="/embed/schedules-view" key="embed-schedule">
          <Schedule />
        </IframeView>
        <Redirect path="/embed/price-cards" to="/home" />

        {/* NOTE: NotFound need to be at the end, on first render path is undefined */}
        {path === undefined ? <AppLoader path=":*" /> : <NotFound path=":*" />}
      </Router>

      <TermsAndConditions />
      <ContactUsModalLoader isOpen={showContactUs} />
      <InstallAppModalLoader isOpen={showInstallApp} />
      <HabitatsUpdater />
      <WhatsNew />
      <ConnectedMembersOnlyWarningModal />
      <ConnectedProductsPrompt />

      <AutoReadNotificationsHandler />
      <GuestExperienceController />
    </Box>
  );

  return routes;
};

export default connect(
  ({
    user: { logged, timezone, lastLogin },
    modals: {
      contactus: { isOpen: showContactUs },
      installApp: { isOpen: showInstallApp },
    },
  }) => ({
    showContactUs,
    showInstallApp,
    logged,
    timezone,
    lastLogin,
  }),
)(Main);
